import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Stethoscope, Clock, Tag, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services?visible_only=true`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <main className="py-12 bg-gradient-to-br from-blue-50 to-cyan-50 min-h-screen" data-testid="services-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
            Our Services
          </h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            Comprehensive, free healthcare services for KGF mine workers and their families. 
            All services are provided at no cost through our charitable trust.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <Card
              key={service.id}
              className="card-hover bg-white border-2 border-gray-100 hover:border-cyan-200 shadow-lg"
              data-testid={`service-card-${index}`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-600 to-emerald-600 rounded-full flex items-center justify-center mb-4">
                    <Stethoscope className="w-7 h-7 text-white" />
                  </div>
                  {service.price === 0 && (
                    <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-3 py-1 rounded-full">
                      FREE
                    </span>
                  )}
                </div>
                <CardTitle className="text-2xl text-gray-900">{service.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <div className="space-y-2 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>Duration: {service.duration} minutes</span>
                  </div>
                  {service.price > 0 && (
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      <span>Price: ₹{service.price}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Features Section */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <Card className="bg-white border-2 border-cyan-100">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Stethoscope className="w-8 h-8 text-cyan-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Expert Care</h3>
              <p className="text-gray-600 text-sm">
                25+ years of specialized medical expertise in cardiology and community health
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border-2 border-emerald-100">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Tag className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Completely Free</h3>
              <p className="text-gray-600 text-sm">
                All services provided at no cost to former mine workers and their families
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border-2 border-blue-100">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">Early Hours</h3>
              <p className="text-gray-600 text-sm">
                Open from 5:00 AM - 11:00 AM, Monday to Saturday for your convenience
              </p>
            </CardContent>
          </Card>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-cyan-600 to-emerald-600 rounded-2xl p-8 md:p-12 text-white text-center shadow-2xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Book an Appointment?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Choose your preferred service and schedule a free consultation
          </p>
          <Link to="/appointments" data-testid="book-appointment-cta">
            <Button size="lg" className="bg-white text-cyan-600 hover:bg-gray-100">
              <Calendar className="w-5 h-5 mr-2" />
              Book Appointment Now
            </Button>
          </Link>
        </div>
      </div>
    </main>
  );
};

export default Services;
